import { useParams } from "react-router-dom";
import { useApp } from "../state";
import { Avatar } from "../components/Avatar";
import { PageHeader } from "../components/PageHeader";
import { TeamBadge } from "../components/TeamBadge";
import { formChipClass, formatLocalDay, pointsChipClass } from "../util";

export function Profile() {
  const { state, me, viewerRole } = useApp();
  const { id } = useParams();
  const targetId = id || me?.id || (viewerRole === "spectator" ? "sangu" : undefined);

  if (!targetId) {
    return (
      <div className="page">
        <div className="loading">Not signed in.</div>
      </div>
    );
  }

  const player = state.players.find((p) => p.id === targetId);
  const profile = state.profiles[targetId];
  const lbEntry = state.leaderboard.find((e) => e.player_id === targetId);

  if (!player || !profile || !lbEntry) {
    return <div className="page"><div className="loading">Player not found.</div></div>;
  }

  const funFactText = funFactFor(targetId, state);
  return (
    <div className="page">
      <PageHeader
        title={player.name}
        tone="purple"
        leading={<Avatar name={player.name} initials={player.initials} color={player.color} />}
        sub={`Sitting #${lbEntry.rank} · ${profile.total} glorious pts`}
      />

      <div>
        <div className="section-label">FORM · LAST {profile.form.length}</div>
        <div className="form-chips">
          {profile.form.length === 0 && <div className="meta">No graded picks yet.</div>}
          {profile.form.map((entry, i) => (
            <span key={i} className={formChipClass(entry.points, entry.max_points, entry.missing_award)}>{entry.points}</span>
          ))}
        </div>
      </div>

      <div className="stat-grid">
        <StatTile num={profile.exact_count} label="exact predictions" />
        <StatTile num={profile.goal_difference_count} label="exact GDs" />
        <StatTile num={profile.correct_calls} label="correct calls" />
        <StatTile num={profile.missed_predictions} label="missed picks" />
        <StatTile num={profile.score_error} label="total score error" />
        <StatTile num={`${Math.round(profile.accuracy * 100)}%`} label="accuracy" />
      </div>

      {funFactText && (
        <div className="card card--dashed">
          <div className="pill">FUN FACT</div>
          <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>{funFactText}</div>
        </div>
      )}

      {profile.recent_picks.length > 0 && (
        <>
          <div className="section-label" style={{ marginTop: 8 }}>MATCH HISTORY</div>
          {profile.recent_picks.map((rp) => (
            <div className="card row" key={rp.match_id}>
              <div style={{ flex: 1 }}>
                <div className="recent-fixture"><TeamBadge code={rp.home} compact /> <span>vs</span> <TeamBadge code={rp.away} compact /></div>
                <div style={{ fontSize: 11, color: "var(--ink-dim)" }}>
                  {formatLocalDay(rp.kickoff_utc)} · {rp.round}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>
                {rp.pick
                  ? `${rp.pick.home}-${rp.pick.away}${rp.pick.winner ? ` → ${rp.pick.winner === "home" ? rp.home : rp.away}` : ""}`
                  : rp.missing_limit_exhausted
                    ? "No pick · allowance used"
                    : rp.missing_award ? "No pick · group minimum" : "—"}
                {rp.actual && (
                  <span style={{ marginLeft: 6, color: "var(--ink)" }}>
                    ({rp.actual.home}-{rp.actual.away})
                  </span>
                )}
              </div>
              <span className={pointsChipClass(rp.points, rp.max_points)}>
                {rp.points > 0 ? `+${rp.points}` : "0"}
              </span>
            </div>
          ))}
        </>
      )}

    </div>
  );
}

function StatTile({ num, label }: { num: number | string; label: string }) {
  return (
    <div className="card stat-card">
      <div className="stat-num">{num}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function funFactFor(playerId: string, state: ReturnType<typeof useApp>["state"]): string | null {
  const facts: string[] = [];
  const onlyYou = state.fun_facts.only_you_hits[playerId] ?? 0;
  if (onlyYou > 0) {
    facts.push(`Only player to nail ${onlyYou} exact score${onlyYou === 1 ? "" : "s"} so far.`);
  }
  const longest = state.fun_facts.longest_streaks[playerId] ?? 0;
  if (longest >= 3) {
    facts.push(`Longest scoring streak: ${longest} matches.`);
  }
  if (state.fun_facts.biggest_jump?.player_id === playerId) {
    const { delta, matchday } = state.fun_facts.biggest_jump;
    facts.push(`Biggest match haul: +${delta} pts on ${matchday}.`);
  }
  const contrarianCount = state.fun_facts.contrarian_badges.filter(b => b.player_id === playerId).length;
  if (contrarianCount > 0) {
    facts.push(`Called ${contrarianCount} winner${contrarianCount === 1 ? "" : "s"} no one else saw coming.`);
  }
  if (facts.length === 0) return null;
  // Pick one deterministically based on player id (so it doesn't flicker on rebuild)
  return facts[playerId.length % facts.length];
}
